/**
 * The repeating group, as the card editor sees it (KPR-679; epic KPE-027).
 *
 * A repeating group is ONE element on a card that holds an ordered list of
 * entries, each with the group's own columns: the ingredients of a recipe, the
 * steps of a procedure, the lines of an invoice. In the interface the group's
 * own elements are COLUMNS and each repeat is an ENTRY ("row" stays an API and
 * ingest word).
 *
 * MIRROR of keepr-api utils/repeatingGroups.js (KPR-675), which holds the
 * rules and refuses what breaks them. The numbers and the lists below are the
 * ones the API's contract serves (`limits.repeatingGroup`); the editor shows
 * them and withholds what the API would refuse, so an author is told BEFORE a
 * save rather than after one. Nothing here is a second authority: when the API
 * moves a limit, this file moves in the same release.
 *
 * Shape (SCHEMA.md § repeating-group):
 *
 *   { name, label, dataType: 'repeating-group',
 *     options: { help?, group?, duplicate?,
 *       columns:    [ { name, label, dataType, options }, ... ]   1..200, ordered
 *       minEntries: whole number, 0..maxEntries        (absent = 0)
 *       maxEntries: whole number, 1..500               (absent = 100)
 *       sections:   true | false  (heading entries; absent = off) } }
 */

export const REPEATING_GROUP = 'repeating-group';

/** The most entries an item holds when the author sets none. */
export const DEFAULT_MAX_ENTRIES = 100;
/** The most an author may raise the maximum to. */
export const MAX_ENTRIES_CEILING = 500;
/** The most columns one group holds (matches a blueprint card's 200 elements). */
export const MAX_COLUMNS = 200;

export const isRepeatingGroupType = dataType => dataType === REPEATING_GROUP;
export const isRepeatingGroup = element => !!element && element.dataType === REPEATING_GROUP;

/** A group's columns as stored: an array, or [] for anything else. */
export const columnsOf = element => (Array.isArray(element?.options?.columns) ? element.options.columns : []);

/**
 * The types a COLUMN cannot be, and the sentence that says why (the API's
 * `nested_repeating_group` and `column_type_not_allowed`). The editor's column
 * pickers do not offer them; the sentence stands where the choice would have
 * been.
 */
export const NESTING_SENTENCE = 'A repeating group holds one level of columns, so a group cannot sit inside another group. Add its columns to this group, or give the card a second group.';
export const COLUMN_TYPE_REASONS = Object.freeze({
	[REPEATING_GROUP]: NESTING_SENTENCE,
	confirmations: 'A “Me too” count belongs to the whole item: keepr counts the people who confirmed the item, and an entry has no one confirming it.',
});
export const COLUMN_TYPES_EXCLUDED = Object.freeze(Object.keys(COLUMN_TYPE_REASONS));

/**
 * What a GLOBAL card's group cannot have as a column (keepr-api KPR-692,
 * `lookup_out_of_scope`): a card lookup. A global card points at no other card,
 * and keepr's check for that reads a card's elements, not a group's columns —
 * so the editor does not offer the type, and says why under the column's
 * picker. A group on a collection's card keeps it, for the cards of its own
 * collection.
 */
export const GLOBAL_COLUMN_TYPES_EXCLUDED = Object.freeze(['card-lookup']);
export const GLOBAL_COLUMN_LOOKUP_SENTENCE = 'A global card points at no other card, so a column of its group cannot look one up. Look the card up from an element of the card instead.';

/**
 * The sentence a card in use (a global card that is, or a published
 * collection's card others look up) adds to what it cannot do, once it holds a
 * repeating group (keepr-api KPR-692): its columns follow the rule that
 * elements do, and its limits cannot tighten past what an item holds.
 */
export const GROUP_ONLY_GROWS_SENTENCE = 'A repeating group can gain columns, but a column cannot be removed or retyped, and its most entries cannot go below (nor its fewest above) what an item holds.';

/**
 * The option keys a column cannot carry (the API's COLUMN_REFUSED_OPTIONS),
 * each because what honours it reads an item's own elements only. The element
 * editor withholds them in a column; opening one later is a change that only
 * grows what a column can be. `drivenFrom` opened in KPR-686 — as a
 * CALCULATION over the entry and its item, never driven from another card
 * (the column's driven-value control offers Calculated alone).
 */
export const COLUMN_OPTION_KEYS_EXCLUDED = Object.freeze([
	'requiredWhen', 'isTitle', 'group', 'duplicate', 'sequence', 'unique',
	'quickFilter', 'followable', 'extendedLength', 'filter', 'strict', 'identity', 'rangeEnd',
	'convertFrom', 'convertTo', 'convertValues',
	// `mask` was withheld here by KPR-1348 and is offered since KPR-1353: a
	// short text, long text or number column may be masked, and the group's
	// read view, table cell, entry form and History mask it
	// (utils/maskedElements.js).
]);

/** The group's own options, edited in its panel (RepeatingGroupOptions). */
export const GROUP_PANEL_OPTIONS = Object.freeze(['columns', 'minEntries', 'maxEntries', 'sections']);

/** The every-type option keys a GROUP itself refuses (say it with `minEntries`). */
export const GROUP_REFUSED_KEYS = Object.freeze(['required', 'requiredWhen', 'drivenFrom', 'isTitle']);

/** The effective most entries of a group: the author's, else the default. */
export const maxEntriesOf = (element) => {
	const value = element?.options?.maxEntries;
	return Number.isInteger(value) && value >= 1 && value <= MAX_ENTRIES_CEILING ? value : DEFAULT_MAX_ENTRIES;
};
